import { useState } from "react";
import useIsomorphicLayoutEffect from "./useIsomorphicLayoutEffect";

// an on-screen keyboard takes a large slice of the screen; the mobile URL bar
// collapsing moves the visual viewport by far less
const KEYBOARD_MIN_INSET = 120;

// Only the keyboard needs JS: a fixed overlay is already sized to the small
// viewport, so it survives the URL bar on its own, while nothing in CSS tracks
// the keyboard — a dialog centred in the layout viewport ends up underneath it.
// Returns null while no keyboard is up so callers keep their CSS sizing and do
// not re-centre on every browser-chrome change.
export default function useKeyboardViewport(enabled = true) {
  const [viewport, setViewport] = useState(null);

  useIsomorphicLayoutEffect(() => {
    const visualViewport = window.visualViewport;
    if (!enabled || !visualViewport) return;

    const update = () => {
      const next =
        window.innerHeight - visualViewport.height >= KEYBOARD_MIN_INSET
          ? {
              height: visualViewport.height,
              offsetTop: visualViewport.offsetTop,
            }
          : null;

      // resize and scroll fire in bursts, so a fresh object every time would
      // re-render the overlay for values that never changed
      setViewport((current) =>
        current?.height === next?.height &&
        current?.offsetTop === next?.offsetTop
          ? current
          : next
      );
    };

    update();
    visualViewport.addEventListener("resize", update);
    visualViewport.addEventListener("scroll", update);

    return () => {
      visualViewport.removeEventListener("resize", update);
      visualViewport.removeEventListener("scroll", update);
      setViewport(null);
    };
  }, [enabled]);

  return viewport;
}
